import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { AriaProvider } from "./context/AriaContext";
import AriaFab from "./components/AriaFab";
import AriaDebugPanel from "./components/AriaDebugPanel";
import SessionExpiredDialog from "./components/SessionExpiredDialog";
import InstallPrompt from "./components/InstallPrompt";
import ProtectedRoute from "./components/ProtectedRoute";
import MobileRouteGuard from "./components/MobileRouteGuard";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import SetPassword from "./pages/SetPassword";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import QuickAssist from "./pages/QuickAssist";
import Module from "./pages/Module";
import CaseHistory from "./pages/CaseHistory";
import Account from "./pages/Account";
import PaymentSuccess from "./pages/PaymentSuccess";
import Patients from "./pages/Patients";
import PatientForm from "./pages/PatientForm";
import PatientDetail from "./pages/PatientDetail";
import ConsultationDetail from "./pages/ConsultationDetail";
import AdminReviews from "./pages/AdminReviews";
import AdminReviewDetail from "./pages/AdminReviewDetail";
import AdminAuditLogs from "./pages/AdminAuditLogs";
import NewCaseAnalysis from "./pages/NewCaseAnalysis";
import CaseAnalysisDetail from "./pages/CaseAnalysisDetail";
import Baa from "./pages/Baa";
import Terms from "./pages/Terms";
import DisclaimerLegal from "./pages/DisclaimerLegal";
import AcceptBaa from "./pages/AcceptBaa";
import QuickAssessmentList from "./pages/QuickAssessmentList";
import QuickAssessmentDetail, { QuickAssessmentNew } from "./pages/QuickAssessment";
import CollabCases from "./pages/CollabCases";
import CollabCaseDetail from "./pages/CollabCaseDetail";
import SpecialistPortal from "./pages/SpecialistPortal";
import AdminRedFlags from "./pages/AdminRedFlags";
import TmdAssessment from "./pages/TmdAssessment";
import TmdAssessmentDetail from "./pages/TmdAssessmentDetail";
import PerioProstho from "./pages/PerioProstho";
import PerioProsthoDetail from "./pages/PerioProsthoDetail";
import TreatmentPlanNew from "./pages/TreatmentPlanNew";
import TreatmentPlanDetail from "./pages/TreatmentPlanDetail";
import CasePresentation from "./pages/CasePresentation";
import PatientPortal from "./pages/PatientPortal";
import InsuranceFees from "./pages/InsuranceFees";
import ReimburseProClaims from "./pages/ReimburseProClaims";
import Analytics from "./pages/Analytics";
import TopologyAnalysisNew from "./pages/TopologyAnalysisNew";
import TopologyAnalysisDetail from "./pages/TopologyAnalysisDetail";
import SecurityAdmin from "./pages/SecurityAdmin";
import Enroll2FA from "./pages/Enroll2FA";
import PlansMembership from "./pages/PlansMembership";
import SelfAttestation from "./pages/SelfAttestation";

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AriaProvider>
            <Toaster theme="dark" position="top-right" richColors />
            <AriaFab />
            <AriaDebugPanel />
            <SessionExpiredDialog />
            <InstallPrompt />
            <MobileRouteGuard>
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/baa" element={<Baa />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/legal/disclaimer" element={<DisclaimerLegal />} />
            <Route path="/disclaimer" element={<DisclaimerLegal />} />
            {/* Public Specialist Portal — token-authenticated, no JWT required */}
            <Route path="/collab/portal/:token" element={<SpecialistPortal />} />
            {/* Public Patient Portal — token-authenticated, no JWT required */}
            <Route path="/patient-portal/:token" element={<PatientPortal />} />
            <Route
              path="/accept-baa"
              element={
                <ProtectedRoute>
                  <AcceptBaa />
                </ProtectedRoute>
              }
            />
            <Route path="/register" element={<Navigate to="/signup" replace />} />
            <Route path="/sign-in" element={<Navigate to="/login" replace />} />
            <Route path="/sign-up" element={<Navigate to="/signup" replace />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quick-assist"
              element={
                <ProtectedRoute>
                  <QuickAssist />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans-membership"
              element={
                <ProtectedRoute>
                  <PlansMembership />
                </ProtectedRoute>
              }
            />
            <Route
              path="/self-attestation"
              element={
                <ProtectedRoute>
                  <SelfAttestation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/module/:slug"
              element={
                <ProtectedRoute>
                  <Module />
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute>
                  <CaseHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients"
              element={
                <ProtectedRoute>
                  <Patients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/new"
              element={
                <ProtectedRoute>
                  <PatientForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/:id"
              element={
                <ProtectedRoute>
                  <PatientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/:id/edit"
              element={
                <ProtectedRoute>
                  <PatientForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/consultation/:id"
              element={
                <ProtectedRoute>
                  <ConsultationDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reviews"
              element={
                <ProtectedRoute>
                  <AdminReviews />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reviews/:id"
              element={
                <ProtectedRoute>
                  <AdminReviewDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit-logs"
              element={
                <ProtectedRoute>
                  <AdminAuditLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/red-flags"
              element={
                <ProtectedRoute>
                  <AdminRedFlags />
                </ProtectedRoute>
              }
            />
            <Route
              path="/case-analysis/new"
              element={
                <ProtectedRoute>
                  <NewCaseAnalysis />
                </ProtectedRoute>
              }
            />
            <Route
              path="/case-analysis/:id"
              element={
                <ProtectedRoute>
                  <CaseAnalysisDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quick-assessment"
              element={
                <ProtectedRoute>
                  <QuickAssessmentList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quick-assessment/new"
              element={
                <ProtectedRoute>
                  <QuickAssessmentNew />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quick-assessment/:id"
              element={
                <ProtectedRoute>
                  <QuickAssessmentDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collab-cases"
              element={
                <ProtectedRoute>
                  <CollabCases />
                </ProtectedRoute>
              }
            />
            <Route
              path="/collab-cases/:id"
              element={
                <ProtectedRoute>
                  <CollabCaseDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tmd-assessment/new"
              element={<ProtectedRoute><TmdAssessment /></ProtectedRoute>}
            />
            <Route
              path="/tmd-assessment/:id"
              element={<ProtectedRoute><TmdAssessmentDetail /></ProtectedRoute>}
            />
            <Route
              path="/perio-prostho/new"
              element={<ProtectedRoute><PerioProstho /></ProtectedRoute>}
            />
            <Route
              path="/perio-prostho/:id"
              element={<ProtectedRoute><PerioProsthoDetail /></ProtectedRoute>}
            />
            <Route
              path="/treatment-plan/new"
              element={<ProtectedRoute><TreatmentPlanNew /></ProtectedRoute>}
            />
            <Route
              path="/treatment-plan/:id"
              element={<ProtectedRoute><TreatmentPlanDetail /></ProtectedRoute>}
            />
            <Route
              path="/case-presentation/:plan_id"
              element={<ProtectedRoute><CasePresentation /></ProtectedRoute>}
            />
            <Route
              path="/insurance-fees"
              element={<ProtectedRoute><InsuranceFees /></ProtectedRoute>}
            />
            <Route
              path="/claims"
              element={<ProtectedRoute><ReimburseProClaims /></ProtectedRoute>}
            />
            <Route
              path="/analytics"
              element={<ProtectedRoute><Analytics /></ProtectedRoute>}
            />
            <Route
              path="/topology-analysis/new"
              element={<ProtectedRoute><TopologyAnalysisNew /></ProtectedRoute>}
            />
            <Route
              path="/topology-analysis/:id"
              element={<ProtectedRoute><TopologyAnalysisDetail /></ProtectedRoute>}
            />
            <Route
              path="/security-admin"
              element={<ProtectedRoute><SecurityAdmin /></ProtectedRoute>}
            />
            <Route
              path="/enroll-2fa"
              element={<ProtectedRoute><Enroll2FA /></ProtectedRoute>}
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <Account />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            {/* Catch-all: redirect unknown paths to landing */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </MobileRouteGuard>
          </AriaProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
